/**
 * 실행 상태의 화면 표현 — 낱말·관형형·색 톤·아이콘의 정본 하나. 키는 세션 어휘(`SimStatus` — «상태 모름» 포함)다.
 *
 * 실행 상태가 보이는 화면은 모두 여기서 읽는다 — 실행 기록(실행 목록 카드·플랫폼 홈 최근 활동·프로젝트 홈과 모델 홈의 기록 표·분석 화면의
 * 기록 패널·재시작 원본 고르기), 실행 중인 것(실행 목록의 실행 중 카드·실행 화면의 배지·진행 막대·모델 탭의 점), 끝난 사유 알림,
 * 설정의 활동 로그. 화면마다 지도를 따로 두었을 때 나중에 생긴 상태(강제 종료)가 아이콘 없이 그려졌고, 같은 상태가 화면마다 다른 색이었다
 * (완료가 카드는 초록·패널은 파랑 — 스테이지 실측 2026-10-15; 실행 중이 실행 중 카드는 초록·기록 카드는 파랑 — D-106).
 * 상태가 늘면 이 표 하나가 는다(타입이 강제한다). 아이콘 컴포넌트는 RunStatusIcon.tsx.
 */
import type { ComponentType } from 'react';
import type { SvgIconProps } from '@mui/material';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import StopCircleOutlinedIcon from '@mui/icons-material/StopCircleOutlined';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import DoNotDisturbOnOutlinedIcon from '@mui/icons-material/DoNotDisturbOnOutlined';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import type { SimulationEntry } from '@/types/project';
import { isSimStatus, type SimStatus } from '@/types/simulation';

/** MUI 팔레트의 상태 색 이름 — Chip·SvgIcon 의 color 로 그대로 쓴다 */
export type RunStatusTone = 'info' | 'success' | 'warning' | 'error';

interface RunStatusView {
  /** 낱말(명사) — 칩·배지·묶음 라벨 */
  label: string;
  /** 관형형 — 문장에 끼울 때(«…된 실행»). 명사 라벨에 «된» 을 손으로 잇지 않는다 */
  adjective: string;
  tone: RunStatusTone;
  Icon: ComponentType<SvgIconProps>;
}

/** 상태 → 낱말·관형형·톤·아이콘 */
export const RUN_STATUS_VIEW: Readonly<Record<SimStatus, RunStatusView>> = {
  // 준비 중 — Build 를 넘긴 뒤 결과를 알기 전. 실행 중과 같은 진행색, 모양으로 가른다
  building: { label: '준비 중', adjective: '준비 중인', tone: 'info', Icon: HourglassEmptyIcon },
  running: { label: '실행 중', adjective: '실행 중인', tone: 'info', Icon: PlayCircleOutlineIcon },
  paused: { label: '일시정지', adjective: '일시정지된', tone: 'warning', Icon: PauseCircleOutlineIcon },
  completed: { label: '완료', adjective: '완료된', tone: 'success', Icon: CheckCircleOutlineIcon },
  stopped: { label: '정지', adjective: '정지된', tone: 'warning', Icon: StopCircleOutlinedIcon },
  failed: { label: '실패', adjective: '실패한', tone: 'error', Icon: CancelOutlinedIcon },
  // 판정 없이 정리됨 — 실패와 같은 경보색, 모양으로 가른다
  terminated: { label: '강제 종료', adjective: '강제 종료된', tone: 'error', Icon: DoNotDisturbOnOutlinedIcon },
  // 상태 모름 — 응답 없는 참가자, 어휘 밖의 기록 원문, 주소만으로 연 실행. 주의색, 모양으로 가른다
  unknown: { label: '상태 모름', adjective: '상태를 모르는', tone: 'warning', Icon: HelpOutlineIcon },
};

export const runStatusLabel = (status: SimStatus): string => RUN_STATUS_VIEW[status].label;
export const runStatusAdjective = (status: SimStatus): string => RUN_STATUS_VIEW[status].adjective;
export const runStatusTone = (status: SimStatus): RunStatusTone => RUN_STATUS_VIEW[status].tone;

/** 톤의 팔레트 자리 — 칠한 바탕(`light`)·그 위 글(`dark`)·막대·점(`main`). 배지·진행 막대·모델 탭 점이 쓴다 */
export const runStatusPalette = (status: SimStatus): { bg: string; fg: string; main: string } => {
  const tone = runStatusTone(status);
  return { bg: `${tone}.light`, fg: `${tone}.dark`, main: `${tone}.main` };
};

/**
 * 이력 항목의 상태 라벨 — 서버가 상태를 실어 보낸 항목만(rawStatus). 원문이 없는 항목(URL 만으로 합성한 것)은 상태를 말하지 않는다(null).
 */
export function entryStatusLabel(entry: Pick<SimulationEntry, 'status' | 'rawStatus'>): string | null {
  return entry.rawStatus ? runStatusLabel(entry.status) : null;
}

/**
 * 원문 상태 글의 낱말 — 세션 어휘면 위 표의 낱말, `extra` 가 아는 원문이면 그 낱말, 그 밖은 원문 그대로(감추지도 지어내지도 않는다).
 * 세션 어휘 밖의 원문을 함께 싣는 표면(활동 로그 — 시작되지 않은 실행·운전 엔진의 실행)이 쓴다.
 */
export function rawStatusLabel(raw: string, extra: Readonly<Record<string, string>> = {}): string {
  const status = raw.trim().toLowerCase();
  if (isSimStatus(status)) return runStatusLabel(status);
  return Object.prototype.hasOwnProperty.call(extra, status) ? extra[status] : raw;
}

/** 디자인 토큰(theme/tokens.css)의 상태 색 — 팔레트 대신 토큰으로 칠하는 표면(분석 화면)용. 톤과 한 쌍이다 */
export const RUN_TONE_TOKEN: Record<RunStatusTone, string> = {
  info: 'var(--state-info)',
  success: 'var(--state-ok)',
  warning: 'var(--state-warning)',
  error: 'var(--state-alarm)',
};
